// LT-341: Agent catalogue UI (v1).
//
// Operator-facing surface for the /agents REST API shipped by LT-338.
// v1 covers browse + clone-to-custom + deprecate. Capability editing
// and the full version timeline are deferred to a v2 follow-up per
// the LT-341 light-LLD (Confluence page 22675587).
//
// Permission gates per LT-338 LLD §6:
//   - System preset, any user: see Clone.
//   - Workspace custom, admin: see Clone + Deprecate.
//   - Workspace custom, owner (non-admin): see Clone (Edit deferred).
//   - Workspace custom, other non-admin: read-only.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Copy, Trash2, AlertTriangle, Pencil, ChevronRight, Plus, Mic } from 'lucide-react';

import PageShell from '../../components/PageShell';
import { Button, Card, Pill, EmptyState, Input, Select, Checkbox, Textarea } from '../../ui';
import { useAuth } from '../../context/AuthContext';
import { Agent, AgentCreate } from '../../types/agent';
import agentsService from '../../services/agentsService';

interface CloneModalState {
  open: boolean;
  source: Agent | null;
  name: string;
  submitting: boolean;
  error: string | null;
}

interface ConfirmState {
  open: boolean;
  target: Agent | null;
  submitting: boolean;
  error: string | null;
}

// LT-385: rename + description-edit modal state. Workspace-custom only;
// system presets stay immutable per the backend 403.
interface EditMetaState {
  open: boolean;
  target: Agent | null;
  name: string;
  description: string;
  submitting: boolean;
  error: string | null;
}

const ALL_CATEGORIES_VALUE = '__all__';

const AgentsPage: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORIES_VALUE);
  const [includeDeprecated, setIncludeDeprecated] = useState<boolean>(false);
  const [cloneModal, setCloneModal] = useState<CloneModalState>({
    open: false,
    source: null,
    name: '',
    submitting: false,
    error: null,
  });
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    target: null,
    submitting: false,
    error: null,
  });
  const [editMeta, setEditMeta] = useState<EditMetaState>({
    open: false,
    target: null,
    name: '',
    description: '',
    submitting: false,
    error: null,
  });

  const reload = async () => {
    setLoadError(null);
    try {
      const rows = await agentsService.list({
        includeSystem: true,
        includeDeprecated,
      });
      setAgents(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load agents');
      setAgents([]);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeprecated]);

  const filtered = useMemo(() => {
    if (!agents) return [];
    return agents.filter((a) => {
      if (categoryFilter !== ALL_CATEGORIES_VALUE && a.category !== categoryFilter) {
        return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!a.name.toLowerCase().includes(q) && !a.category.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [agents, categoryFilter, search]);

  const categories = useMemo(() => {
    if (!agents) return [];
    const set = new Set<string>();
    agents.forEach((a) => set.add(a.category));
    return Array.from(set).sort();
  }, [agents]);

  const canDeprecate = (agent: Agent): boolean => {
    if (agent.is_system) return false;
    if (agent.deprecated_at) return false;
    return isAdmin();
  };

  // LT-385: workspace-custom Agents allow rename/description edit. The
  // backend permits owner OR admin (services.agents.update_agent → router
  // _require_admin_or_owner). We mirror that here so non-owners on the
  // catalogue don't see a button that would 403.
  const canEditMetadata = (agent: Agent): boolean => {
    if (agent.is_system) return false;
    if (agent.deprecated_at) return false;
    if (isAdmin()) return true;
    return Boolean(user?.id && agent.created_by_user_id === user.id);
  };

  const openEditMeta = (target: Agent) =>
    setEditMeta({
      open: true,
      target,
      name: target.name,
      description: target.description ?? '',
      submitting: false,
      error: null,
    });

  const closeEditMeta = () =>
    setEditMeta({
      open: false,
      target: null,
      name: '',
      description: '',
      submitting: false,
      error: null,
    });

  const submitEditMeta = async () => {
    if (!editMeta.target) return;
    const trimmedName = editMeta.name.trim();
    if (!trimmedName) {
      setEditMeta((s) => ({ ...s, error: 'Name is required' }));
      return;
    }
    setEditMeta((s) => ({ ...s, submitting: true, error: null }));
    try {
      // Send only fields that actually changed — backend treats this as
      // metadata-only and skips the version bump (LT-385).
      const payload: { name?: string; description?: string | null } = {};
      if (trimmedName !== editMeta.target.name) payload.name = trimmedName;
      const trimmedDescription = editMeta.description.trim();
      const previousDescription = editMeta.target.description ?? '';
      if (trimmedDescription !== previousDescription) {
        payload.description = trimmedDescription || null;
      }
      if (Object.keys(payload).length === 0) {
        closeEditMeta();
        return;
      }
      await agentsService.update(editMeta.target.id, payload);
      closeEditMeta();
      await reload();
    } catch (err) {
      setEditMeta((s) => ({
        ...s,
        submitting: false,
        error: err instanceof Error ? err.message : 'Save failed',
      }));
    }
  };

  const openCloneModal = (source: Agent) => {
    setCloneModal({
      open: true,
      source,
      name: `Copy of ${source.name}`,
      submitting: false,
      error: null,
    });
  };

  const closeCloneModal = () =>
    setCloneModal({ open: false, source: null, name: '', submitting: false, error: null });

  const submitClone = async () => {
    if (!cloneModal.source || !cloneModal.name.trim()) return;
    setCloneModal((s) => ({ ...s, submitting: true, error: null }));
    try {
      const payload: AgentCreate = {
        name: cloneModal.name.trim(),
        category: cloneModal.source.category,
        prompt_template: cloneModal.source.current_version?.prompt_template ?? '',
        voice_id_male: cloneModal.source.current_version?.voice_id_male ?? null,
        voice_id_female: cloneModal.source.current_version?.voice_id_female ?? null,
        capabilities: cloneModal.source.current_version?.capabilities ?? [],
        description: cloneModal.source.description ?? null,
      };
      await agentsService.create(payload);
      closeCloneModal();
      await reload();
    } catch (err) {
      setCloneModal((s) => ({
        ...s,
        submitting: false,
        error: err instanceof Error ? err.message : 'Clone failed',
      }));
    }
  };

  const openConfirm = (target: Agent) =>
    setConfirm({ open: true, target, submitting: false, error: null });

  const closeConfirm = () =>
    setConfirm({ open: false, target: null, submitting: false, error: null });

  const submitDeprecate = async () => {
    if (!confirm.target) return;
    setConfirm((s) => ({ ...s, submitting: true, error: null }));
    try {
      await agentsService.deprecate(confirm.target.id);
      closeConfirm();
      await reload();
    } catch (err) {
      setConfirm((s) => ({
        ...s,
        submitting: false,
        error: err instanceof Error ? err.message : 'Deprecate failed',
      }));
    }
  };

  return (
    <PageShell>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">
            Catalogue
          </div>
          <h1 className="text-2xl font-semibold text-slate-50 mb-1">Agents</h1>
          <p className="text-slate-300">
            System presets and workspace-custom agents. Clone a preset to start
            your own — that's the fastest path. Use{' '}
            <span className="font-medium text-slate-200">+ New custom agent</span> if
            you want to start from a blank slate instead.
          </p>
        </div>
        {/* LT-412: header CTA links to the new from-blank flow. Clone-from-
            preset stays the primary creation path on every preset card. */}
        <Button
          intent="secondary"
          onClick={() => navigate('/agents/new')}
          data-testid="agents-new-custom"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          <span className="ml-1.5">New custom agent</span>
        </Button>
      </div>

      <Card padding="default" className="mb-6">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <label className="text-xs text-slate-400 block mb-1">Search</label>
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name or category"
              data-testid="agents-search"
            />
          </div>
          <div className="md:w-56">
            <label className="text-xs text-slate-400 block mb-1">Category</label>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="agents-category-filter"
            >
              <option value={ALL_CATEGORIES_VALUE}>All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={includeDeprecated}
              onChange={(e) => setIncludeDeprecated(e.target.checked)}
              data-testid="agents-include-deprecated"
            />
            <span className="text-sm text-slate-300">Show deprecated</span>
          </label>
        </div>
      </Card>

      {loadError && (
        <Card padding="default" className="mb-6">
          <div className="flex items-start gap-3 text-danger-text">
            <AlertTriangle className="w-5 h-5 mt-0.5" strokeWidth={2} />
            <div>
              <div className="font-medium">Failed to load agents</div>
              <div className="text-sm text-slate-300">{loadError}</div>
            </div>
          </div>
        </Card>
      )}

      {agents !== null && filtered.length === 0 && !loadError && (
        <EmptyState
          surface="agents_list"
          icon={Bot}
          headline="No agents match your filters"
          subhead="Try clearing the search or showing deprecated agents."
          primary={{
            label: 'Clear filters',
            onClick: () => {
              setSearch('');
              setCategoryFilter(ALL_CATEGORIES_VALUE);
              setIncludeDeprecated(false);
            },
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            isOwner={Boolean(user?.id && agent.created_by_user_id === user.id)}
            onClone={() => openCloneModal(agent)}
            onDeprecate={canDeprecate(agent) ? () => openConfirm(agent) : undefined}
            onEditMetadata={canEditMetadata(agent) ? () => openEditMeta(agent) : undefined}
          />
        ))}
      </div>

      {cloneModal.open && cloneModal.source && (
        <Modal onClose={cloneModal.submitting ? undefined : closeCloneModal}>
          <h2 className="text-lg font-semibold text-slate-50 mb-1">
            Clone agent
          </h2>
          <p className="text-sm text-slate-300 mb-4">
            Creates a workspace-custom copy of <strong>{cloneModal.source.name}</strong>.
            You can edit the clone after creating it.
          </p>
          <label className="text-xs text-slate-400 block mb-1">Name</label>
          <Input
            type="text"
            value={cloneModal.name}
            onChange={(e) => setCloneModal((s) => ({ ...s, name: e.target.value }))}
            data-testid="clone-name-input"
            disabled={cloneModal.submitting}
          />
          {cloneModal.error && (
            <div className="text-sm text-danger-text mt-3">{cloneModal.error}</div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              intent="ghost"
              onClick={closeCloneModal}
              disabled={cloneModal.submitting}
            >
              Cancel
            </Button>
            <Button
              intent="primary"
              onClick={submitClone}
              disabled={cloneModal.submitting || !cloneModal.name.trim()}
              data-testid="clone-submit"
            >
              {cloneModal.submitting ? 'Cloning…' : 'Clone'}
            </Button>
          </div>
        </Modal>
      )}

      {editMeta.open && editMeta.target && (
        <Modal onClose={editMeta.submitting ? undefined : closeEditMeta}>
          <h2 className="text-lg font-semibold text-slate-50 mb-1">Edit agent</h2>
          <p className="text-sm text-slate-300 mb-4">
            Rename or update the description of <strong>{editMeta.target.name}</strong>.
            Metadata-only edits do not bump the agent version.
          </p>
          <label className="text-xs text-slate-400 block mb-1">Name</label>
          <Input
            type="text"
            value={editMeta.name}
            onChange={(e) => setEditMeta((s) => ({ ...s, name: e.target.value }))}
            data-testid="edit-meta-name-input"
            disabled={editMeta.submitting}
          />
          <label className="text-xs text-slate-400 block mb-1 mt-3">Description</label>
          <Textarea
            value={editMeta.description}
            onChange={(e) => setEditMeta((s) => ({ ...s, description: e.target.value }))}
            data-testid="edit-meta-description-input"
            rows={3}
            disabled={editMeta.submitting}
          />
          {editMeta.error && (
            <div className="text-sm text-danger-text mt-3">{editMeta.error}</div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              intent="ghost"
              onClick={closeEditMeta}
              disabled={editMeta.submitting}
            >
              Cancel
            </Button>
            <Button
              intent="primary"
              onClick={submitEditMeta}
              disabled={editMeta.submitting || !editMeta.name.trim()}
              data-testid="edit-meta-submit"
            >
              {editMeta.submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Modal>
      )}

      {confirm.open && confirm.target && (
        <Modal onClose={confirm.submitting ? undefined : closeConfirm}>
          <h2 className="text-lg font-semibold text-slate-50 mb-1">
            Deprecate agent?
          </h2>
          <p className="text-sm text-slate-300 mb-4">
            <strong>{confirm.target.name}</strong> will be hidden from the catalogue
            but existing pinned references keep working. This can be reversed by
            recreating a clone.
          </p>
          {confirm.error && (
            <div className="text-sm text-danger-text mb-3">{confirm.error}</div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              intent="ghost"
              onClick={closeConfirm}
              disabled={confirm.submitting}
            >
              Cancel
            </Button>
            <Button
              intent="danger"
              onClick={submitDeprecate}
              disabled={confirm.submitting}
              data-testid="deprecate-submit"
            >
              {confirm.submitting ? 'Deprecating…' : 'Deprecate'}
            </Button>
          </div>
        </Modal>
      )}
    </PageShell>
  );
};

interface AgentCardProps {
  agent: Agent;
  isOwner: boolean;
  onClone: () => void;
  onDeprecate?: () => void;
  // LT-385: undefined hides the button (system preset / non-eligible viewer).
  onEditMetadata?: () => void;
}

// LT-412: catalogue card — capability badges removed (parked pending
// Voice Gateway tool-call support, design §7). Shows category + voice
// + a small vN badge. Preset cards lead with the Clone CTA — that is
// the primary creation path.
const AgentCard: React.FC<AgentCardProps> = ({ agent, onClone, onDeprecate, onEditMetadata }) => {
  const versionNumber = agent.current_version?.version_number ?? null;
  const voiceMale = agent.current_version?.voice_id_male ?? null;
  const voiceFemale = agent.current_version?.voice_id_female ?? null;
  const voiceLabel = (() => {
    const parts: string[] = [];
    if (voiceMale) parts.push('male');
    if (voiceFemale) parts.push('female');
    if (parts.length === 0) return null;
    return parts.join(' + ');
  })();

  return (
    <Card padding="default" data-testid={`agent-card-${agent.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-slate-50 truncate">
            {agent.name}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">{agent.category}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          {versionNumber !== null && (
            <Pill intent="neutral" data-testid={`version-pill-${agent.id}`}>
              <span className="font-mono">v{versionNumber}</span>
            </Pill>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-3">
        <Pill intent={agent.is_system ? 'info' : 'accent'}>
          {agent.is_system ? 'System preset' : 'Custom'}
        </Pill>
        {voiceLabel && (
          <Pill intent="neutral" data-testid={`voice-pill-${agent.id}`}>
            <Mic className="h-3 w-3" strokeWidth={2} />
            <span className="ml-1">{voiceLabel}</span>
          </Pill>
        )}
        {agent.deprecated_at && <Pill intent="warn">Deprecated</Pill>}
      </div>

      {agent.description && (
        <p className="text-sm text-slate-300 mb-3 line-clamp-2">
          {agent.description}
        </p>
      )}

      <div className="flex gap-2 justify-end pt-2 border-t border-white/[0.08]">
        <Link
          to={`/agents/${agent.id}`}
          className="inline-flex items-center text-sm text-slate-300 hover:text-slate-50 px-2 py-1.5"
          data-testid={`details-${agent.id}`}
        >
          Details
          <ChevronRight className="w-4 h-4 ml-0.5" strokeWidth={2} />
        </Link>
        {onEditMetadata && (
          <Button
            intent="ghost"
            size="sm"
            onClick={onEditMetadata}
            data-testid={`edit-meta-${agent.id}`}
          >
            <Pencil className="w-4 h-4" strokeWidth={2} />
            <span className="ml-1.5">Edit</span>
          </Button>
        )}
        {/* LT-412: preset cards lead with Clone (primary intent); workspace
            custom cards keep it as a quieter ghost option. */}
        <Button
          intent={agent.is_system ? 'primary' : 'ghost'}
          size="sm"
          onClick={onClone}
          data-testid={`clone-${agent.id}`}
        >
          <Copy className="w-4 h-4" strokeWidth={2} />
          <span className="ml-1.5">Clone</span>
        </Button>
        {onDeprecate && (
          <Button
            intent="danger"
            size="sm"
            onClick={onDeprecate}
            data-testid={`deprecate-${agent.id}`}
          >
            <Trash2 className="w-4 h-4" strokeWidth={2} />
            <span className="ml-1.5">Deprecate</span>
          </Button>
        )}
      </div>
    </Card>
  );
};

interface ModalProps {
  children: React.ReactNode;
  onClose?: () => void;
}

const Modal: React.FC<ModalProps> = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    onClick={onClose}
    data-testid="modal-backdrop"
  >
    <div
      className="bg-surface-1 border border-white/[0.08] rounded-ds-xl shadow-ds-e3 p-6 w-full max-w-md mx-4"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

export default AgentsPage;
